import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Fuse from 'fuse.js';
import coneMascot from '../assets/da2026/cone.png';

function TournamentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [phases, setPhases] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [currentPhaseId, setCurrentPhaseId] = useState(1);
  const [searchTeam, setSearchTeam] = useState('');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuse, setFuse] = useState(null);
  const [classements, setClassements] = useState({});
  const [activeTab, setActiveTab] = useState('matchs'); // 'matchs' | 'classement'
  const [followedTeam, setFollowedTeam] = useState(() => {
    try { return localStorage.getItem('abrf_followed_team') || ''; } catch (e) { return ''; }
  });
  // Passe a true des que la phase par defaut a ete choisie, pour ne plus toucher a
  // l'onglet une fois que le visiteur navigue lui-meme entre les phases.
  const defaultPhaseApplied = useRef(false);

  useEffect(() => {
    defaultPhaseApplied.current = false;
    fetchTournamentData();
    fetchPhases();
  }, [id]);

  useEffect(() => {
    if (matches.length > 0) {
      const fuseOptions = {
        keys: ['team1_name', 'team2_name'],
        threshold: 0.3,
        ignoreLocation: true,
      };
      setFuse(new Fuse(matches, fuseOptions));
    }
  }, [matches]);

  const fetchPhases = async () => {
    try {
      const { data: phasesData, error: phasesError } = await supabase
        .from('phase')
        .select('*');

      if (phasesError) {
        console.error('Erreur phases:', phasesError);
        throw phasesError;
      }

      const { data: descriptionsData, error: descriptionsError } = await supabase
        .from('description_phase')
        .select('*')
        .eq('id_tournois', Number(id));

      if (descriptionsError) {
        console.error('Erreur descriptions phases:', descriptionsError);
      }

      if (typeof window !== 'undefined') {
        window.__lastDescriptionsData = descriptionsData;
      }

      const phasesWithDescriptions = phasesData.map(phase => {
        const foundDesc = descriptionsData?.find(desc => desc.id_phase === phase.id);
        return { ...phase, description: foundDesc?.description || '' };
      });

      setPhases(phasesWithDescriptions);
    } catch (error) {
      console.error('Erreur lors du chargement des phases:', error);
    }
  };

  // Phase ouverte a l'arrivee sur un tournoi : la plus avancee dont les matchs sont
  // reellement definis (equipes renseignees), donc finales > brassages > poules.
  // Les matchs recus ici sont deja filtres sur equipe1_texte : leur simple presence
  // suffit a considerer la phase comme lancee.
  // On ne se sert plus de `tournois.current_phase` : cette colonne vaut 3 en base pour
  // tous les tournois et faisait atterrir les visiteurs sur des finales vides.
  // On ignore aussi les phases que l'organisation n'a pas encore ouvertes, sinon on
  // ouvrirait un onglet qui n'affiche que « pas encore disponibles ».
  const appliquerPhaseParDefaut = (tournoi, matchsDefinis) => {
    if (defaultPhaseApplied.current || !tournoi) return;

    const estVisible = {
      1: tournoi.affichage_poules === true,
      2: tournoi.affichage_brassages === true,
      3: tournoi.affichage_finales === true,
    };
    const aDesMatchs = phaseId => matchsDefinis.some(match => match.phase_id === phaseId);

    const phase =
      [3, 2, 1].find(phaseId => estVisible[phaseId] && aDesMatchs(phaseId)) ||
      [1, 2, 3].find(phaseId => estVisible[phaseId]) ||
      1;

    setCurrentPhaseId(phase);
    defaultPhaseApplied.current = true;
  };

  const fetchTournamentData = async () => {
    try {
      const { data: rawMatchesData, error: matchesError } = await supabase
        .from('match')
        .select('*')
        .eq('tournoi_id', id);

      if (matchesError) {
        console.error('Erreur matches:', matchesError);
        throw matchesError;
      }

      const matchesData = rawMatchesData.filter(match =>
        match.equipe1_texte && match.equipe1_texte.trim() !== ''
      );

      if (!matchesData || matchesData.length === 0) {
        console.log("Aucun match valide trouvé après filtrage (equipe1_texte manquant).");
        setMatches([]);
      }

      let tournamentRow = null;
      try {
        let { data: tournamentData, error: tournamentError } = await supabase
          .from('tournois')
          .select('*')
          .eq('id', id)
          .single();

        if (tournamentError) {
          ({ data: tournamentData, error: tournamentError } = await supabase
            .from('tournoi')
            .select('*')
            .eq('id', id)
            .single());
        }

        if (!tournamentError) {
          tournamentRow = tournamentData;
          setTournament(tournamentData);
        }
      } catch (tournamentError) {
        console.error('Exception lors de la récupération du tournoi:', tournamentError);
      }

      const groupeIds = [...new Set(matchesData.map(match => match.groupe_id).filter(id => id !== null))];
      const terrainIds = [...new Set(matchesData.map(match => match.terrain_id).filter(id => id !== null))];

      let equipeIds = [];
      if (matchesData && matchesData.length > 0) {
        equipeIds = matchesData.map(match => [match.equipe1_id, match.equipe2_id]).flat();
      }

      const { data: classementData, error: classementError } = await supabase
        .from('classement')
        .select('*')
        .in('groupe_id', groupeIds.length > 0 ? groupeIds : [null])
        .eq('tournoi_id', id);

      if (classementError) {
        console.error('Erreur classement:', classementError);
      }

      if (classementData) {
        const classementEquipeIds = classementData.map(item => item.equipe_id);
        equipeIds.push(...classementEquipeIds);
      }

      const uniqueEquipeIds = [...new Set(equipeIds.filter(id => id != null))];

      let equipesData = [];
      if (uniqueEquipeIds.length > 0) {
        const { data: fetchedEquipesData, error: equipesError } = await supabase
          .from('equipe')
          .select('*')
          .in('id', uniqueEquipeIds);

        if (equipesError) {
          console.error('Erreur équipes:', equipesError);
        } else {
          equipesData = fetchedEquipesData || [];
        }
      } else {
        console.log("Aucun ID d'équipe unique à récupérer.");
      }

      const equipesMap = {};
      equipesData.forEach(equipe => { equipesMap[equipe.id] = equipe.nom; });

      const classementsByPhaseAndGroup = {};
      if (classementData) {
        classementData.forEach(item => {
          const phaseId = item.phase_id;
          const groupeId = item.groupe_id;
          if (!classementsByPhaseAndGroup[phaseId]) classementsByPhaseAndGroup[phaseId] = {};
          if (!classementsByPhaseAndGroup[phaseId][groupeId]) classementsByPhaseAndGroup[phaseId][groupeId] = [];
          const classementItem = { ...item, equipe_nom: equipesMap[item.equipe_id] || `Équipe ${item.equipe_id}` };
          classementsByPhaseAndGroup[phaseId][groupeId].push(classementItem);
        });

        Object.keys(classementsByPhaseAndGroup).forEach(phaseId => {
          Object.keys(classementsByPhaseAndGroup[phaseId]).forEach(groupeId => {
            classementsByPhaseAndGroup[phaseId][groupeId].sort((a, b) => a.rang - b.rang);
          });
        });
      }

      setClassements(classementsByPhaseAndGroup);

      let terrainsMap = {};
      if (terrainIds.length > 0) {
        const { data: terrainsData, error: terrainsError } = await supabase
          .from('terrain')
          .select('*')
          .in('id', terrainIds);

        if (!terrainsError && terrainsData) {
          terrainsData.forEach(terrain => { terrainsMap[terrain.id] = terrain.nom || `Terrain ${terrain.id}`; });
        } else {
          console.error('Erreur terrains:', terrainsError);
          terrainIds.forEach(id => { terrainsMap[id] = `Terrain ${id}`; });
        }
      }

      let groupesData = [];
      if (groupeIds.length === 0) {
        setGroupes([]);
      } else {
        const { data: fetchedGroupesData, error: groupesError } = await supabase
          .from('groupe')
          .select('*')
          .in('id', groupeIds);

        if (groupesError) {
          console.error('Erreur groupes:', groupesError);
          setGroupes([]);
        } else {
          groupesData = fetchedGroupesData || [];
          if (groupesData.length === 0) {
            groupesData = groupeIds.map((id, index) => ({ id, nom: `Groupe ${id}`, ordre_affichage: index + 1 }));
          }
          groupesData = groupesData.map((groupe, index) => {
            if (groupe.ordre_affichage === undefined || groupe.ordre_affichage === null) {
              return { ...groupe, ordre_affichage: index + 1 };
            }
            return groupe;
          });
          setGroupes(groupesData);
        }
      }

      const groupesMap = {};
      groupesData.forEach(groupe => {
        const nomGroupe = groupe.nom || groupe.name || groupe.nom_du_groupe || groupe.titre || 'Groupe sans nom';
        groupesMap[groupe.id] = nomGroupe;
      });

      const matchesWithNames = matchesData.map(match => {
        const groupeNom = match.groupe_id && groupesMap[match.groupe_id] ? groupesMap[match.groupe_id] : 'Sans groupe';
        const terrainNom = match.terrain_id && terrainsMap[match.terrain_id] ? terrainsMap[match.terrain_id] : 'Terrain inconnu';
        return {
          ...match,
          team1_name: equipesMap[match.equipe1_id] || match.equipe1_texte,
          team2_name: equipesMap[match.equipe2_id] || match.equipe2_texte,
          groupe_nom: groupeNom,
          terrain_nom: terrainNom
        };
      });

      setMatches(matchesWithNames || []);
      appliquerPhaseParDefaut(tournamentRow, matchesWithNames || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredMatches = () => {
    if (!searchTeam.trim() || !fuse) return matches;
    const searchResults = fuse.search(searchTeam);
    return searchResults.map(result => result.item);
  };

  const organizeMatchesByGroup = (matches) => {
    const matchesByGroup = {};
    const matchesByPhase = matches.filter(match => match.phase_id === currentPhaseId);

    matchesByPhase.forEach(match => {
      let groupId = 'sans_groupe';
      let groupName = 'Sans groupe';
      let orderDisplay = 9999;

      if (match.groupe_id) {
        groupId = match.groupe_id;
        groupName = match.groupe_nom || 'Groupe sans nom';
        const groupe = groupes.find(g => g.id === match.groupe_id);
        if (groupe && (groupe.ordre_affichage !== undefined)) {
          orderDisplay = groupe.ordre_affichage;
        }
      }

      if (!matchesByGroup[groupId]) {
        matchesByGroup[groupId] = { id: groupId, name: groupName, orderDisplay, matches: [] };
      }
      matchesByGroup[groupId].matches.push(match);
    });

    Object.values(matchesByGroup).forEach(group => {
      group.matches.sort((a, b) => (a.identifiant_match || '').localeCompare(b.identifiant_match || ''));
    });

    return Object.values(matchesByGroup).sort((a, b) => a.orderDisplay - b.orderDisplay);
  };

  const getMatchStatus = (match) => {
    if (match.score_equipe1 !== null && match.score_equipe1 !== undefined &&
        match.score_equipe2 !== null && match.score_equipe2 !== undefined) {
      return 'terminé';
    }
    return 'à venir';
  };

  const filteredMatches = getFilteredMatches();
  const matchesByGroup = organizeMatchesByGroup(filteredMatches);

  const allTeams = React.useMemo(() => {
    const set = new Set();
    matches.forEach(m => { if (m.team1_name) set.add(m.team1_name); if (m.team2_name) set.add(m.team2_name); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [matches]);

  const chooseTeam = (name) => {
    setFollowedTeam(name);
    try { localStorage.setItem('abrf_followed_team', name); } catch (e) {}
  };
  const clearTeam = () => {
    setFollowedTeam('');
    try { localStorage.removeItem('abrf_followed_team'); } catch (e) {}
  };
  const isFollowed = (name) => followedTeam && name === followedTeam;

  const winnerOf = (m) => {
    if (getMatchStatus(m) !== 'terminé') return null;
    const s1 = Number(m.score_equipe1), s2 = Number(m.score_equipe2);
    if (s1 > s2) return 1;
    if (s2 > s1) return 2;
    return 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  const meta = (tournament?.description || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean)[0] || '';
  const currentPhase = phases.find(phase => phase.id === currentPhaseId);
  const currentPhaseDescription = currentPhase?.description || '';
  const sortedPhases = [...phases].sort((a, b) => a.id - b.id);

  let shouldDisplayPhase = false;
  if (tournament) {
    if (currentPhaseId === 1) shouldDisplayPhase = tournament.affichage_poules === true;
    else if (currentPhaseId === 2) shouldDisplayPhase = tournament.affichage_brassages === true;
    else if (currentPhaseId === 3) shouldDisplayPhase = tournament.affichage_finales === true;
  }

  // Groupes de classement pour la phase courante (indépendant de la recherche)
  const phaseClassements = classements[currentPhaseId] || {};
  const classementGroups = Object.keys(phaseClassements).map(gid => {
    const g = groupes.find(x => String(x.id) === String(gid));
    return {
      id: gid,
      // La colonne en base est nom_du_groupe : sans elle on retombait sur l'id brut
      // et la poule 1 s'affichait « Groupe 8 ».
      name: (g && (g.nom || g.name || g.nom_du_groupe || g.titre)) || `Groupe ${gid}`,
      order: (g && g.ordre_affichage) || 9999,
      rows: phaseClassements[gid],
    };
  }).sort((a, b) => a.order - b.order);

  const StatusChip = ({ status }) =>
    status === 'terminé' ? (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-green/12 text-brand-green">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-green"></span> Terminé
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-orange-soft text-brand-orange-deep">
        <i className="ri-time-line"></i> À venir
      </span>
    );

  const MatchCard = ({ match }) => {
    const status = getMatchStatus(match);
    const win = winnerOf(match);
    return (
      <div className="rounded-2xl border border-brand-line bg-white overflow-hidden shadow-soft">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-brand-line">
          <StatusChip status={status} />
          <span className="text-[11px] font-semibold text-brand-muted flex items-center gap-1.5">
            <i className="ri-map-pin-fill text-brand-orange"></i>
            {match.terrain_nom}{match.heure ? ` · ${match.heure}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2.5 px-4 py-3.5">
          <div className="flex-1 flex flex-col gap-0.5 min-w-0">
            {/* truncate doit porter sur un element non-flex : text-overflow est ignore
                sur un conteneur flex, le nom deborderait alors sur le score. */}
            <span className={`font-bold text-sm flex items-center gap-1 min-w-0 max-w-full ${win === 1 ? 'text-brand-orange-deep' : 'text-brand-ink'}`}>
              {isFollowed(match.team1_name) && <i className="ri-star-fill text-brand-orange text-xs shrink-0"></i>}
              <span className="truncate">{match.team1_name}</span>
            </span>
            {win === 1 && <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Vainqueur</span>}
          </div>

          {status === 'terminé' ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="font-display text-xl min-w-[38px] text-center bg-brand-orange-soft text-brand-orange-deep rounded-lg px-1.5 py-1.5">{match.score_equipe1}</span>
              <span className="font-display text-brand-muted">–</span>
              <span className="font-display text-xl min-w-[38px] text-center bg-brand-orange-soft text-brand-orange-deep rounded-lg px-1.5 py-1.5">{match.score_equipe2}</span>
            </div>
          ) : (
            <div className="font-display text-brand-muted text-sm px-3 py-1.5 bg-[#F7F1EB] rounded-lg shrink-0">VS</div>
          )}

          <div className="flex-1 flex flex-col items-end text-right gap-0.5 min-w-0">
            <span className={`font-bold text-sm flex items-center gap-1 min-w-0 max-w-full ${win === 2 ? 'text-brand-orange-deep' : 'text-brand-ink'}`}>
              <span className="truncate">{match.team2_name}</span>
              {isFollowed(match.team2_name) && <i className="ri-star-fill text-brand-orange text-xs shrink-0"></i>}
            </span>
            {win === 2 && <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Vainqueur</span>}
          </div>
        </div>
      </div>
    );
  };

  const Board = ({ rows }) => {
    if (!rows || rows.length === 0) {
      return <div className="card p-4 text-center text-sm text-brand-muted">Aucun classement disponible</div>;
    }
    return (
      <div className="flex flex-col gap-2">
        {rows.map((item, index) => {
          const ga = Number(item.goal_average || 0);
          const lead = index === 0;
          const followed = isFollowed(item.equipe_nom);
          return (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-3.5 py-3 rounded-2xl border ${
                lead
                  ? 'bg-gradient-to-br from-[#FFF3E1] to-[#FFE7CF] border-[#F59B3B]/50 shadow-[0_10px_24px_rgba(245,155,59,0.22)]'
                  : followed
                  ? 'bg-white border-brand-orange/45'
                  : 'bg-white border-brand-line'
              }`}
            >
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-display text-sm shrink-0 ${lead ? 'bg-gradient-to-br from-[#F59B3B] to-brand-orange-deep text-white' : 'bg-[#F7F1EB] text-brand-muted'}`}>
                {lead ? <i className="ri-trophy-fill"></i> : item.rang}
              </span>
              <span className="flex-1 font-bold text-sm text-brand-ink flex items-center gap-1.5 min-w-0">
                {followed && <i className="ri-star-fill text-brand-orange text-xs shrink-0"></i>}
                <span className="truncate">{item.equipe_nom}</span>
              </span>
              <span className={`text-[11px] font-bold px-2 py-1 rounded-lg ${ga >= 0 ? 'bg-brand-green/12 text-brand-green' : 'bg-black/[0.06] text-brand-muted'}`}>
                {ga > 0 ? '+' : ''}{ga}
              </span>
              <span className="font-display text-base text-brand-ink text-right leading-none min-w-[42px]">
                {item.points || 0}
                <small className="block font-sans text-[9px] font-bold text-brand-muted uppercase tracking-wide mt-0.5">pts</small>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-md mx-auto pb-20">
      {/* ===== HERO ===== */}
      <header className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white overflow-hidden rounded-b-3xl shadow-hero px-5 pt-8 pb-5">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>
        <button
          onClick={() => navigate('/tournaments')}
          className="w-9 h-9 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center text-xl active:scale-95 transition-transform"
          aria-label="Retour aux tournois"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        {meta && (
          <div className="flex items-center gap-2 mt-4 text-[11px] font-bold uppercase tracking-[0.16em] text-white/90">
            <span className="w-1.5 h-1.5 rounded-full bg-white"></span> {meta}
          </div>
        )}
        <h1 className="h-display text-3xl mt-2 leading-[0.95]">{tournament?.nom || tournament?.name || `Tournoi ${id}`}</h1>

        <div className="mt-4 flex items-center gap-2.5 bg-white rounded-2xl px-4 py-3 shadow-card">
          <i className="ri-search-line text-brand-orange text-lg"></i>
          <input
            type="text"
            placeholder="Rechercher mon équipe…"
            value={searchTeam}
            onChange={(e) => setSearchTeam(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-brand-ink placeholder:text-brand-muted"
          />
          {searchTeam && (
            <button onClick={() => setSearchTeam('')} className="text-brand-muted" aria-label="Effacer la recherche">
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
      </header>

      {/* ===== STICKY NAV : onglets + phases ===== */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-brand-line px-5 py-3">
        <div className="seg">
          <button className={`seg-item ${activeTab === 'matchs' ? 'seg-item-on' : ''}`} onClick={() => setActiveTab('matchs')}>Matchs</button>
          <button className={`seg-item ${activeTab === 'classement' ? 'seg-item-on' : ''}`} onClick={() => setActiveTab('classement')}>Classement</button>
        </div>
        <div className="flex gap-2 mt-2.5 overflow-x-auto no-sb">
          {sortedPhases.map(phase => (
            <button
              key={phase.id}
              onClick={() => setCurrentPhaseId(phase.id)}
              className={`shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full border whitespace-nowrap transition-colors ${
                currentPhaseId === phase.id ? 'bg-brand-ink text-white border-brand-ink' : 'bg-white text-brand-muted border-brand-line'
              }`}
            >
              {phase.nom}
            </button>
          ))}
        </div>
        {currentPhaseDescription && (
          <p className="text-[11px] text-brand-muted italic mt-2">{currentPhaseDescription}</p>
        )}
      </div>

      {/* ===== ÉQUIPE SUIVIE ===== */}
      <div className="mx-5 mt-4 flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-brand-orange-soft to-white border border-brand-orange/25">
        <span className="w-10 h-10 rounded-xl bg-brand-orange text-white flex items-center justify-center text-xl shrink-0">
          <i className="ri-team-fill"></i>
        </span>
        {followedTeam ? (
          <>
            <div className="min-w-0 flex-1">
              <b className="h-display text-sm text-brand-ink block leading-tight truncate">{followedTeam}</b>
              <span className="text-[11px] text-brand-muted">
                Équipe suivie (sur cet appareil) · <button onClick={clearTeam} className="underline font-semibold">changer</button>
              </span>
            </div>
            <i className="ri-star-fill text-brand-orange text-xl shrink-0"></i>
          </>
        ) : (
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wide text-brand-orange-deep mb-1">Suis ton équipe</div>
            <select
              value=""
              onChange={(e) => e.target.value && chooseTeam(e.target.value)}
              className="w-full bg-white border border-brand-line rounded-lg text-sm px-2 py-1.5 text-brand-ink outline-none"
            >
              <option value="" disabled>Choisir mon équipe…</option>
              {allTeams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* ===== CONTENU ===== */}
      <div className="px-5 pt-5 space-y-6">
        {!shouldDisplayPhase ? (
          <div className="card p-6 text-center flex flex-col items-center">
            <div className="w-14 h-14 rounded-full bg-brand-orange-soft flex items-center justify-center mb-3">
              <i className="ri-lock-line text-2xl text-brand-orange-deep"></i>
            </div>
            <p className="text-brand-muted">Les infos de cette phase ne sont pas encore disponibles !</p>
          </div>
        ) : activeTab === 'matchs' ? (
          matchesByGroup.length === 0 ? (
            <div className="card p-6 text-center flex flex-col items-center">
              <img src={coneMascot} alt="" className="w-16 h-16 object-contain mb-3 opacity-90" />
              <p className="text-brand-muted">
                {searchTeam ? `Aucun match trouvé pour « ${searchTeam} » dans cette phase` : 'Aucun match pour cette phase'}
              </p>
            </div>
          ) : (
            matchesByGroup.map(group => (
              <div key={group.id}>
                <h2 className="h-display text-lg text-brand-ink mb-3">{group.name}</h2>
                <div className="flex flex-col gap-3">
                  {group.matches.map(match => <MatchCard key={match.id} match={match} />)}
                </div>
              </div>
            ))
          )
        ) : (
          classementGroups.length === 0 ? (
            <div className="card p-6 text-center flex flex-col items-center">
              <img src={coneMascot} alt="" className="w-16 h-16 object-contain mb-3 opacity-90" />
              <p className="text-brand-muted">Aucun classement pour cette phase</p>
            </div>
          ) : (
            classementGroups.map(group => (
              <div key={group.id}>
                <h2 className="h-display text-lg text-brand-ink mb-3">{group.name}</h2>
                <Board rows={group.rows} />
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}

export default TournamentDetails;
